import { useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import {
  UniversalFilterOperationTypeStatus,
  UniversalFilterOperationTypeValue,
  UniversalFilterOperationTypeModes,
  UniversalFilterOperationTypeFilterState,
  UniversalFilterOperationTypeOnChange
} from '../../UniversalFilterOperationType.types';

interface UniversalFilterOperationTypeFilterStateOptions {
  onChange: UniversalFilterOperationTypeOnChange;
}

function useUniversalFilterOperationTypeFilterState({
  onChange
}: UniversalFilterOperationTypeFilterStateOptions) {
  // All modes and options available in handleApplyFilterState
  // must be implemented in handleParseFilterValue too
  const handleApplyFilterState = useCallback<
    (filterState: UniversalFilterOperationTypeFilterState) => void
  >(
    (filterState) => {
      const states = filterState?.states;

      if (filterState?.mode === UniversalFilterOperationTypeModes.IS_ANY_OF) {
        onChange(isEmpty(states) ? undefined : { in: states });
        return;
      }

      if (filterState?.mode === UniversalFilterOperationTypeModes.IS_NONE_OF) {
        onChange(isEmpty(states) ? undefined : { notIn: states });
        return;
      }

      onChange();
    },
    [onChange]
  );

  const handleParseFilterValue = useCallback<
    (
      filterValue: UniversalFilterOperationTypeValue
    ) => UniversalFilterOperationTypeFilterState | undefined
  >((filterValue) => {
    if (isNil(filterValue)) {
      return undefined;
    }

    if (!isEmpty(filterValue.in)) {
      return {
        mode: UniversalFilterOperationTypeModes.IS_ANY_OF,
        states: filterValue.in as UniversalFilterOperationTypeStatus[]
      };
    }

    if (!isEmpty(filterValue.notIn)) {
      return {
        mode: UniversalFilterOperationTypeModes.IS_NONE_OF,
        states: filterValue.notIn as UniversalFilterOperationTypeStatus[]
      };
    }

    return undefined;
  }, []);

  return { handleApplyFilterState, handleParseFilterValue };
}

export default useUniversalFilterOperationTypeFilterState;
